import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock, Database, Search, BarChart3, Menu, X } from 'lucide-react';

const Navigation = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { path: '/services/sovereign-ai', icon: Lock, title: 'Private LLM Hosting' },
    { path: '/services/precision-rag', icon: Search, title: 'Precision RAG' },
    { path: '/services/semantic-lakehouse', icon: Database, title: 'AI Data Lakes' },
    { path: '/services/agentic-dashboard', icon: BarChart3, title: 'Agentic Dashboards' },
  ];

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white shadow-md border-b border-gray-200'
          : 'bg-white/95 backdrop-blur-xl border-b border-gray-100'
      }`}
    >
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <img
              src="/beemind-logo.svg"
              alt="BeeMind AI Logo"
              className="h-9 object-contain"
            />
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`group relative inline-block text-sm font-medium transition-colors duration-300 ${
                location.pathname === '/' ? 'text-gray-900' : 'text-gray-500/90 hover:text-[#f97316]'
              }`}
            >
              Home
              <span
                className={`absolute -bottom-1 left-1/2 h-[2px] -translate-x-1/2 bg-gradient-to-r from-[#f97316] to-[#fbce16] transition-all duration-300 ${
                  location.pathname === '/' ? 'w-full' : 'w-0 group-hover:w-full'
                }`}
              />
            </Link>
            <div className="relative group">
              <Link to="/services" className="group relative text-sm font-medium text-gray-500/90 hover:text-[#f97316] transition-colors duration-300 flex items-center gap-1">
                Solutions
                <svg className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                <span className="absolute -bottom-1 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-gradient-to-r from-[#f97316] to-[#fbce16] transition-all duration-300 group-hover:w-full" />
              </Link>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-white border border-gray-200 rounded-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 shadow-xl shadow-gray-200/50 overflow-hidden">
                {services.map((service) => {
                  const Icon = service.icon;
                  return (
                    <Link
                      key={service.path}
                      to={service.path}
                      className="flex items-center space-x-3 px-5 py-3.5 hover:bg-gray-50 transition-colors"
                    >
                      <Icon className="w-4 h-4 text-orange-500" />
                      <span className="text-sm text-gray-700">{service.title}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
            <Link
              to="/#contact"
              className="group relative inline-block text-sm font-medium text-gray-500/90 hover:text-[#f97316] transition-colors duration-300"
            >
              Contact
              <span className="absolute -bottom-1 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-gradient-to-r from-[#f97316] to-[#fbce16] transition-all duration-300 group-hover:w-full" />
            </Link>
            <Link
              to="/#demo"
              className="inline-flex items-center justify-center px-5 py-2 rounded-lg bg-gradient-to-r from-[#f97316] to-[#fbce16] text-white text-sm font-semibold shadow-lg shadow-orange-500/20 transition-all duration-300 transform hover:scale-105 hover:shadow-orange-500/40"
            >
              Request Demo
            </Link>
          </div>

          <button
            className="md:hidden text-gray-600 hover:text-gray-900"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden py-4 border-t border-gray-200 bg-white">
            <Link to="/" onClick={() => setIsOpen(false)} className="block px-4 py-2.5 text-gray-700 hover:text-[#f97316] text-sm transition-colors">Home</Link>
            <div className="px-4 py-2">
              <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">Solutions</div>
              <Link
                to="/services"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#f97316] transition-colors"
              >
                All Solutions
              </Link>
              {services.map((service) => {
                const Icon = service.icon;
                return (
                  <Link
                    key={service.path}
                    to={service.path}
                    onClick={() => setIsOpen(false)}
                    className="flex items-center space-x-3 px-3 py-2.5 text-gray-700 hover:text-[#f97316] text-sm transition-colors"
                  >
                    <Icon className="w-4 h-4 text-orange-500" />
                    <span>{service.title}</span>
                  </Link>
                );
              })}
            </div>
            <Link to="/#contact" onClick={() => setIsOpen(false)} className="block px-4 py-2.5 text-gray-700 hover:text-[#f97316] text-sm transition-colors">Contact</Link>
            <Link
              to="/#demo"
              onClick={() => setIsOpen(false)}
              className="mt-2 mx-4 inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#f97316] to-[#fbce16] text-white text-sm font-semibold shadow-lg shadow-orange-500/20 transition-all duration-300 transform active:scale-[0.99]"
            >
              Request Demo
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
